"use client";

import { useQueryClient } from "@tanstack/react-query";
import { AuthCredential } from "firebase/auth";
import { ReactNode, createContext, use, useEffect, useMemo, useRef, useState } from "react";
import { useAuthFlow } from "@/components/auth/auth-flow-context";
import { PhoneAuthActions, PhoneAuthContext, PhoneAuthContextValue, PhoneAuthState, PhoneAuthStep } from "@/components/auth/methods/phone/phone-auth-context";
import { AuthCredentialKind, AuthMethodId } from "@/components/auth/types";
import { primePatientClaim } from "@/hooks/patient-claim/use-patient-claim";
import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";
import { PatientSource } from "@/types/enums/patient-source";
import { PatientClaimAuthMode } from "@/types/patient-claim";
import { SelfSchedulePatient } from "@/types/self-schedule";
import { signUpLocationId } from "./sign-up-source";
import { PortalEmailInUseError, PortalPatientAlreadyClaimedError, createPortalAccount, linkExistingPortalAccount, listPatientCandidates } from "@/api/auth/patient-claim";
import { authService } from "@/services/auth-service";
const hasFullName = (p: SelfSchedulePatient) => !!p?.firstName?.trim() && !!p?.lastName?.trim();

/**
 * How this sign-up acquires its Google identity. Sign-up always verifies the
 * phone number first, and phone verification swaps the Firebase session — so
 * the Google session is entered (or re-entered) at the very end, and the two
 * entry points differ only in how that final sign-in happens.
 *
 * - `POPUP`: the user chose "Continue with Google" on the sign-up page and
 *   has not authenticated with Google yet. Open the popup at the end.
 * - `CREDENTIAL`: the user already completed a Google popup elsewhere (the
 *   portal login page, which found no linked patient and continued into
 *   sign-up in place). Re-enter that session silently from the captured
 *   credential, falling back to a popup if it has expired.
 */
export type GoogleSignUpEntry = {
  kind: "POPUP";
} | {
  kind: "CREDENTIAL";
  credential: AuthCredential;
  email?: string | null;
};
interface SignUpAuthContextValue {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  /** Combined entry-form submit: stores credentials and sends the SMS. */
  submitEntry: (phone: string) => Promise<void>;
  /** Non-null when this sign-up finishes with Google instead of a password. */
  googleEntry: GoogleSignUpEntry | null;
  /** Switch an email/password sign-up over to the Google variant. */
  chooseGoogle: () => void;
}
const SignUpAuthContext = createContext<SignUpAuthContextValue | null>(null);
export function useSignUpAuth(): SignUpAuthContextValue {
  const ctx = use(SignUpAuthContext);
  if (!ctx) {
    throw new Error("useSignUpAuth must be used inside <PortalSignUp.Provider>");
  }
  return ctx;
}
export interface SignUpAuthProviderProps {
  children: ReactNode;
  /**
   * Open directly in the Google variant. Pass `{ kind: "POPUP" }` to honour a
   * `?method=google` deep link, or a captured credential to continue a Google
   * sign-in that already happened on another surface.
   */
  googleEntry?: GoogleSignUpEntry | null;
  /**
   * Where the patient started, stamped on the patient only when sign-up
   * creates a new one. Passed in because sign-up always runs under the
   * `PATIENT_SIGN_UP` surface, which says nothing about the entry point.
   */
  patientSource?: PatientSource;
  /** Location carried on the sign-up link, ahead of the booking flow's stored one. */
  primaryLocationId?: string;
}
export function SignUpAuthProvider({
  children,
  googleEntry: initialGoogleEntry = null,
  patientSource,
  primaryLocationId: linkedLocationId
}: SignUpAuthProviderProps) {
  const flow = useAuthFlow();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [enteredCode, setEnteredCode] = useState("");
  const [candidates, setCandidates] = useState<SelfSchedulePatient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<SelfSchedulePatient | null>(null);
  const [forceNewPatient, setForceNewPatient] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [googleEntry, setGoogleEntry] = useState<GoogleSignUpEntry | null>(initialGoogleEntry);
  // Captured at confirmPhoneCode time, while the Firebase session is still
  // the phone user. The Google sign-in later swaps the session, so a fresh
  // getIdToken() would return a Google token — which the backend rejects
  // with "Phone-OTP token must include a verified phone_number".
  const phoneOtpTokenRef = useRef<string | null>(null);
  const active = useRef(true);
  const finalizing = useRef(false);
  const completed = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const isNewPatient = forceNewPatient || !!enteredCode && candidates.length === 0;
  const resetFunnel = () => {
    setPhoneNumber("");
    setEnteredCode("");
    setCandidates([]);
    setSelectedPatient(null);
    setForceNewPatient(false);
    setPassword("");
    setGoogleEntry(initialGoogleEntry);
    phoneOtpTokenRef.current = null;
  };
  const step: PhoneAuthStep = useMemo(() => {
    if (selectedPatient && !hasFullName(selectedPatient)) return PhoneAuthStep.NAME;
    if (!phoneNumber) return PhoneAuthStep.ENTER_PHONE;
    if (!enteredCode) return PhoneAuthStep.ENTER_CODE;
    if (isNewPatient) return PhoneAuthStep.NAME;
    return PhoneAuthStep.PICK_PATIENT;
  }, [phoneNumber, enteredCode, selectedPatient, isNewPatient]);

  /**
   * Enter the Google session that this account will be linked to. A captured
   * credential signs in silently; anything else (including a credential
   * Firebase has since rejected as stale) falls back to the popup.
   */
  const enterGoogleSession = async (entry: GoogleSignUpEntry) => {
    if (entry.kind === "CREDENTIAL") {
      try {
        return await authService.signInWithGoogleCredential(entry.credential);
      } catch {
        // Credential expired or already consumed — the popup still works, it
        // just costs the user one extra click.
      }
    }
    const result = await authService.signInWithGooglePopup();
    if (typeof result === "string") {
      flow.actions.setError(AuthMethodId.PHONE, result);
      throw new Error(result);
    }
    return result.user;
  };
  const finalizeGoogle = async (entry: GoogleSignUpEntry, overrides: {
    patientId?: string;
    firstName?: string;
    lastName?: string;
  }) => {
    const phoneOtpToken = phoneOtpTokenRef.current;
    if (!phoneOtpToken) {
      toast({
        title: "Session expired",
        description: "Please start sign-up again.",
        variant: "error"
      });
      resetFunnel();
      throw new Error("Missing phone-OTP token before Google sign-in.");
    }
    const googleUser = await enterGoogleSession(entry);
    if (!active.current) return;
    let linkedPatient: Awaited<ReturnType<typeof linkExistingPortalAccount>>;
    try {
      linkedPatient = await linkExistingPortalAccount({
        patientId: overrides.patientId,
        phoneOtpToken,
        firstName: overrides.firstName,
        lastName: overrides.lastName,
        email: googleUser.email ?? undefined,
        patientSource,
        primaryLocationId: signUpLocationId(patientSource, linkedLocationId)
      });
      // The mutation response is authoritative. Cancel any candidate probe
      // that started when Firebase switched back to Google before writing it,
      // otherwise its stale NONE response can overwrite this patient.
      if (!active.current) return;
      await primePatientClaim(queryClient, googleUser.uid, linkedPatient);
    } catch (err) {
      if (!active.current) return;
      if (err instanceof PortalPatientAlreadyClaimedError) {
        toast({
          title: "Account already exists",
          description: "This Google account already has portal access. Please sign in instead.",
          variant: "error"
        });
      } else {
        flow.actions.setError(AuthMethodId.PHONE, await parseError(err, "We couldn't finish creating your account. Please try again."));
      }
      throw err;
    }
    if (!active.current) return;
    completed.current = true;
    await flow.meta.onSuccess({
      kind: AuthCredentialKind.GOOGLE,
      user: googleUser,
      patient: linkedPatient,
      patientType: linkedPatient.patientType
    });
  };
  const finalize = async (overrides: {
    patientId?: string;
    firstName?: string;
    lastName?: string;
  }) => {
    if (!active.current || finalizing.current || completed.current) return;
    finalizing.current = true;
    setIsFinalizing(true);
    flow.actions.clearError(AuthMethodId.PHONE);
    // finalizeGoogle and the PortalEmailInUseError branch each surface their
    // own toast / setError — track who already reported so the generic
    // create-failure doesn't double-display on top of a specific message.
    let handledLocally = false;
    try {
      if (googleEntry) {
        await finalizeGoogle(googleEntry, overrides);
        return;
      }
      let createdPatient: Awaited<ReturnType<typeof createPortalAccount>>;
      try {
        createdPatient = await createPortalAccount({
          patientId: overrides.patientId,
          email,
          password,
          firstName: overrides.firstName,
          lastName: overrides.lastName,
          patientSource,
          primaryLocationId: signUpLocationId(patientSource, linkedLocationId)
        });
      } catch (err) {
        if (!active.current) return;
        if (err instanceof PortalEmailInUseError) {
          handledLocally = true;
          // Email already has a Firebase user. We don't disambiguate
          // providers — the most common case is the user's own Google-based
          // provider account, so steer them to Google. The backend pings
          // Slack on this 409 so the team sees anything that isn't the
          // Google case and can triage.
          toast({
            title: "Try signing up with Google",
            description: "This email is already in use. Please use “Continue with Google” to finish sign-up.",
            variant: "error"
          });
          resetFunnel();
          return;
        }
        throw err;
      }
      // The backend minted the email/password user while phone-OTP was still
      // signed in. Switch Firebase sessions silently, then publish the Patient
      // returned by that committed link before the surface navigates/resolves.
      if (!active.current) return;
      await authService.logout(true);
      if (!active.current) return;
      const user = await authService.login(email, password);
      if (!active.current) return;
      await primePatientClaim(queryClient, user.uid, createdPatient);
      if (!active.current) return;
      completed.current = true;
      await flow.meta.onSuccess({
        kind: AuthCredentialKind.PASSWORD,
        user,
        patient: createdPatient,
        patientType: createdPatient.patientType
      });
    } catch (err) {
      if (!active.current) return;
      if (!googleEntry && !handledLocally) {
        flow.actions.setError(AuthMethodId.PHONE, await parseError(err, "We couldn't finish creating your account. Please try again."));
      }
      setSelectedPatient(null);
      throw err;
    } finally {
      finalizing.current = false;
      setIsFinalizing(false);
    }
  };
  const phoneActions: PhoneAuthActions = useMemo(() => {
    // Sign-up creates a portal account, so a candidate who already has one is
    // sent to sign in instead of hitting `patient_already_claimed`.
    const requirePortalLogin = () => {
      toast({
        title: "Account already exists",
        description: "Please sign in with your Patient Portal account instead.",
        variant: "error"
      });
      resetFunnel();
    };
    return {
      submitPhone: async (phone: string) => {
        setIsSendingCode(true);
        try {
          const ok = await flow.actions.runAuth(AuthMethodId.PHONE, () => authService.signInWithPhone(phone));
          if (ok !== null) setPhoneNumber(phone);
        } finally {
          setIsSendingCode(false);
        }
      },
      submitCode: async (code: string) => {
        setIsVerifyingCode(true);
        try {
          const result = await flow.actions.runAuth(AuthMethodId.PHONE, async () => {
            await authService.confirmPhoneCode(code);
            const token = await authService.getCurrentUser()?.getIdToken();
            if (token) phoneOtpTokenRef.current = token;
            setIsLoadingCandidates(true);
            try {
              return await listPatientCandidates();
            } finally {
              setIsLoadingCandidates(false);
            }
          });
          if (result) {
            setEnteredCode(code);
            if (result.authMode === PatientClaimAuthMode.PHONE) {
              setCandidates(result.candidates);
            } else {
              setCandidates([]);
            }
          }
        } finally {
          setIsVerifyingCode(false);
        }
      },
      selectPatient: async (p: SelfSchedulePatient) => {
        if (p.hasPortalAccount) {
          requirePortalLogin();
          return;
        }
        setSelectedPatient(p);
        setForceNewPatient(false);
        if (!hasFullName(p)) return;
        try {
          await finalize({
            patientId: p.id
          });
        } catch {
          // handled in finalize
        }
      },
      completeName: async (firstName: string, lastName: string) => {
        try {
          await finalize({
            patientId: selectedPatient?.id,
            firstName,
            lastName
          });
        } catch {
          // handled in finalize
        }
      },
      requirePortalLogin,
      resend: async () => {
        if (!phoneNumber) return;
        await flow.actions.runAuth(AuthMethodId.PHONE, () => authService.signInWithPhone(phoneNumber));
      },
      startNewPatient: () => {
        setForceNewPatient(true);
        setSelectedPatient(null);
      }
    };
  },
  // `finalize` closes over email/password/selectedPatient/googleEntry/
  // patientSource; refreshing on those is enough to keep the closure current.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [flow.actions, flow.meta, email, password, phoneNumber, selectedPatient, googleEntry, patientSource, linkedLocationId]);
  const phoneState: PhoneAuthState = {
    step,
    phoneNumber,
    enteredCode,
    candidates,
    selectedPatient,
    portalRequiredPatient: null,
    isResolvingSession: false,
    isSendingCode,
    isVerifyingCode,
    isClaiming: isFinalizing,
    isLoadingCandidates
  };
  const phoneCtx: PhoneAuthContextValue = {
    state: phoneState,
    actions: phoneActions
  };
  const signUpCtx = useMemo<SignUpAuthContextValue>(() => ({
    email,
    setEmail,
    password,
    setPassword,
    submitEntry: async (phone: string) => {
      await phoneActions.submitPhone(phone);
    },
    googleEntry,
    chooseGoogle: () => setGoogleEntry({
      kind: "POPUP"
    })
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [email, password, phoneActions, googleEntry]);
  return <PhoneAuthContext.Provider value={phoneCtx}>
      <SignUpAuthContext.Provider value={signUpCtx}>
        {children}
      </SignUpAuthContext.Provider>
    </PhoneAuthContext.Provider>;
}